import React, { useState, useEffect, useCallback } from 'react';
import { FiTrash2, FiSave, FiChevronDown, FiChevronUp, FiEdit2 } from 'react-icons/fi';
import apiService from '../../services/api';
import RichTextEditorTipTap from '../common/RichTextEditor';
import { sanitizeHtmlForViewer } from '../../lib/richtext/htmlSanitizer';
import logger from '../../utils/logger';
import styles from './WeeklyInstructionsManager.module.css';

/**
 * WeeklyInstructionsManager - Manages per-week instructions for weekly submission assignments
 * 
 * @param {Object} props
 * @param {number} props.assignmentId - The assignment ID
 * @param {number} props.maxWeeks - Maximum number of weeks allowed
 * @param {string} props.defaultInstructions - Default instructions from the assignment
 * @param {function} props.onSave - Callback when instructions are saved
 */
function WeeklyInstructionsManager({ assignmentId, maxWeeks = 52, defaultInstructions = '', onSave }) {
  const [weeklyInstructions, setWeeklyInstructions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [expandedWeeks, setExpandedWeeks] = useState(new Set());
  const [editingWeek, setEditingWeek] = useState(null);

  // Fetch existing weekly instructions
  const fetchWeeklyInstructions = useCallback(async () => {
    if (!assignmentId) return;
    
    try {
      setLoading(true);
      setError('');
      
      const response = await apiService.request(`/assignments/${assignmentId}/weekly-instructions`, {
        method: 'GET'
      });
      
      if (response.success) {
        setWeeklyInstructions(response.weeklyInstructions || []);
      } else {
        setError(response.message || 'Failed to load weekly instructions');
      }
    } catch (err) {
      logger.error('Error fetching weekly instructions', { error: err, assignmentId });
      setError('Failed to load weekly instructions');
    } finally {
      setLoading(false);
    }
  }, [assignmentId]);

  useEffect(() => {
    fetchWeeklyInstructions();
  }, [fetchWeeklyInstructions]);

  // Get instruction for a specific week
  const getWeekInstruction = (weekNumber) => {
    const found = weeklyInstructions.find(w => w.week_number === weekNumber);
    return found?.instructions || '';
  };

  // Check if a week has custom instructions
  const hasCustomInstruction = (weekNumber) => {
    return weeklyInstructions.some(w => w.week_number === weekNumber && w.instructions);
  };

  // Toggle week expansion
  const toggleWeekExpansion = (weekNumber) => {
    setExpandedWeeks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(weekNumber)) {
        newSet.delete(weekNumber);
      } else {
        newSet.add(weekNumber);
      }
      return newSet;
    });
  };

  // Update instruction for a specific week
  const updateWeekInstruction = (weekNumber, content) => {
    setWeeklyInstructions(prev => {
      const existing = prev.find(w => w.week_number === weekNumber);
      if (existing) {
        return prev.map(w => 
          w.week_number === weekNumber 
            ? { ...w, instructions: content.content_html || content }
            : w
        );
      } else {
        return [...prev, { 
          week_number: weekNumber, 
          instructions: content.content_html || content 
        }];
      }
    });
  };

  // Save all weekly instructions
  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      const instructionsToSave = weeklyInstructions
        .filter(w => w.instructions && w.instructions.trim())
        .map(w => ({
          week_number: w.week_number,
          instructions: w.instructions
        }));

      const response = await apiService.request(`/assignments/${assignmentId}/weekly-instructions`, {
        method: 'PUT',
        body: JSON.stringify({ weeklyInstructions: instructionsToSave })
      });

      if (response.success) {
        setSuccessMessage('Weekly instructions saved successfully!');
        setWeeklyInstructions(response.weeklyInstructions || []);
        setEditingWeek(null);
        if (onSave) onSave(response.weeklyInstructions);
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.message || 'Failed to save weekly instructions');
      }
    } catch (err) {
      logger.error('Error saving weekly instructions', { error: err, assignmentId });
      setError('Failed to save weekly instructions');
    } finally {
      setSaving(false);
    }
  };

  // Delete instruction for a specific week
  const handleDeleteWeek = async (weekNumber) => {
    if (!window.confirm(`Are you sure you want to delete the custom instructions for Week ${weekNumber}?`)) {
      return;
    }

    try {
      setSaving(true);
      setError('');

      const response = await apiService.request(`/assignments/${assignmentId}/weekly-instructions/${weekNumber}`, {
        method: 'DELETE'
      });

      if (response.success) {
        setWeeklyInstructions(prev => prev.filter(w => w.week_number !== weekNumber));
        setSuccessMessage(`Week ${weekNumber} instructions deleted`);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.message || 'Failed to delete week instructions');
      }
    } catch (err) {
      logger.error('Error deleting week instructions', { error: err, weekNumber });
      setError('Failed to delete week instructions');
    } finally {
      setSaving(false);
    }
  };

  const handleAddWeek = (weekToAdd) => {
    if (!weekToAdd) {
      return;
    }
    if (weeklyInstructions.some(w => w.week_number === weekToAdd)) {
      return;
    }

    setWeeklyInstructions(prev => [...prev, { week_number: weekToAdd, instructions: '' }]);
    setExpandedWeeks(prev => new Set([...prev, weekToAdd]));
    setEditingWeek(weekToAdd);
  };

  // Generate week numbers array
  const weekNumbers = Array.from({ length: maxWeeks }, (_, i) => i + 1);
  const availableWeeks = weekNumbers.filter(weekNum => !hasCustomInstruction(weekNum));

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading weekly instructions...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <p className={styles.subtitle}>
          Define weekly instructions for individual weeks. Weeks without weekly instructions will use the default assignment description.
        </p>
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {successMessage && <div className={styles.success}>{successMessage}</div>}

      <div className={styles.defaultSection}>
        <div className={styles.defaultHeader}>
          <span className={styles.defaultLabel}>Default Assignment Description</span>
        </div>
        <div 
          className={`${styles.defaultContent} ${styles.richContent}`}
          dangerouslySetInnerHTML={{
            __html: sanitizeHtmlForViewer(defaultInstructions || 'No description provided')
          }}
        />
      </div>

      <div className={styles.weeksList}>
        {weekNumbers.slice(0, Math.max(maxWeeks, weeklyInstructions.length)).map(weekNum => {
          const hasCustom = hasCustomInstruction(weekNum);
          const isExpanded = expandedWeeks.has(weekNum);
          const isEditing = editingWeek === weekNum;
          const instruction = getWeekInstruction(weekNum);

          // Only show weeks that have instructions or are being edited
          if (!hasCustom && !isEditing && weekNum > Math.max(...weeklyInstructions.map(w => w.week_number), 0)) {
            return null;
          }

          return (
            <div key={weekNum} className={`${styles.weekItem} ${hasCustom ? styles.hasCustom : ''}`}>
              <div 
                className={styles.weekHeader}
                onClick={() => toggleWeekExpansion(weekNum)}
              >
                <div className={styles.weekInfo}>
                  <span className={styles.weekNumber}>Week {weekNum}</span>
                  {hasCustom ? (
                    <span className={styles.customBadge}>Using Weekly Assignment Description</span>
                  ) : (
                    <span className={styles.defaultBadge}>Using Default Assignment Description</span>
                  )}
                </div>
                <div className={styles.weekActions}>
                  {hasCustom && (
                    <>
                      <button
                        type="button"
                        className={styles.iconButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingWeek(weekNum);
                          setExpandedWeeks(prev => new Set([...prev, weekNum]));
                        }}
                        title="Edit instructions"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        type="button"
                        className={`${styles.iconButton} ${styles.deleteButton}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteWeek(weekNum);
                        }}
                        title="Delete custom instructions"
                      >
                        <FiTrash2 />
                      </button>
                    </>
                  )}
                  {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                </div>
              </div>

              {isExpanded && (
                <div className={styles.weekContent}>
                  {isEditing ? (
                    <div className={styles.editorWrapper}>
                      <RichTextEditorTipTap
                        value={{ content_html: instruction, content_plain: instruction.replace(/<[^>]*>/g, '') }}
                        onChange={(content) => updateWeekInstruction(weekNum, content)}
                        placeholder={`Enter instructions for Week ${weekNum}...`}
                      />
                      <div className={styles.editorActions}>
                        <button
                          type="button"
                          className={styles.cancelButton}
                          onClick={() => {
                            setEditingWeek(null);
                            if (!hasCustom) {
                              setWeeklyInstructions(prev => prev.filter(w => w.week_number !== weekNum));
                            }
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className={styles.saveWeekButton}
                          onClick={async () => {
                            setEditingWeek(null);
                            await handleSave();
                          }}
                        >
                          Save Week
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className={`${styles.instructionPreview} ${styles.richContent}`}
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtmlForViewer(
                          instruction || defaultInstructions || 'No description provided'
                        )
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className={styles.actions}>
        <div className={styles.addControls}>
          <select
            className={styles.weekSelect}
            value=""
            onChange={(e) => {
              const weekNumber = parseInt(e.target.value, 10);
              if (!Number.isNaN(weekNumber)) {
                handleAddWeek(weekNumber);
              }
            }}
            disabled={availableWeeks.length === 0}
          >
            <option value="">+ Add Weekly Instructions</option>
            {availableWeeks.map(weekNum => (
              <option key={weekNum} value={weekNum}>
                Week {weekNum}
              </option>
            ))}
          </select>
        </div>
        
        {editingWeek === null && (
          <button
            type="button"
            className={styles.saveButton}
            onClick={handleSave}
            disabled={saving}
          >
            <FiSave /> {saving ? 'Saving...' : 'Save All Changes'}
          </button>
        )}
      </div>
    </div>
  );
}

export default WeeklyInstructionsManager;
